import { useState } from "react";

import { FollowShipperRequestSource } from "constants/index";
import { useAPI } from "hooks";
import {
  cleanEmails,
  emailsAreValid,
  extractCommaSeparatedEmails,
  getFirstInvalidEmail,
} from "utils";

import { CreateFollowRequest } from "./CreateFollowRequest";
import { chunkArray, processSequentially } from "./utils";

interface Props {
  onFollowRequestsCreatedRefresh: VoidFunction;
}

const BATCH_SIZE = 20;

export function CreateFollowRequestContainer(props: Props) {
  const { onFollowRequestsCreatedRefresh } = props;
  const [error, setError] = useState<string | null>(null);
  const [text, setText] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { api } = useAPI();

  const onSubmit = (rawEmails: string) => {
    const extractedEmails = extractCommaSeparatedEmails(rawEmails);
    const allEmailsValid = emailsAreValid(extractedEmails);

    if (!allEmailsValid) {
      setError(
        `Invalid email ${getFirstInvalidEmail(extractedEmails)}, please check the format and try again.`,
      );
      return;
    }

    const cleanedEmails = cleanEmails(extractedEmails);

    if (cleanedEmails.length === 0) {
      setError("Please enter at least one valid email address.");
      return;
    }

    const emailBatches = chunkArray(cleanedEmails, BATCH_SIZE);

    setIsLoading(true);
    setError(null);

    const sendBatch = async (batch: string[]) =>
      api<unknown>("post", "/follow_requests", {
        emails: batch,
        source: FollowShipperRequestSource.EMAIL_INPUT_NETWORK_PAGE,
      });

    processSequentially(emailBatches, sendBatch)
      .then(() => {
        setError(null);
        setText("");
        onFollowRequestsCreatedRefresh();
      })
      .catch((e: unknown) => {
        interface ErrorResponse {
          response?: { data?: { message?: string } };
        }
        const errorMessage = (e as ErrorResponse)?.response?.data?.message;
        setError(errorMessage ?? "Failed to send follow invites.");
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  return (
    <CreateFollowRequest
      onSubmit={onSubmit}
      error={error}
      text={text}
      onTextChange={setText}
      isLoading={isLoading}
    />
  );
}
